import React, { useState } from "react";
import { Grid, Button, FormControl, InputLabel, Select, CircularProgress, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const GSTPurchaseReport2 = ({ customers }) => {
  const [loadingGstPurchase, setLoadingGstPurchase] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [gstPurchaseFilters, setGstPurchaseFilters] = useState({
    dateFrom: null,
    dateTo: null,
  });

  const generateGstPurchaseReport = async (exportType = 'html') => {
    const params = new URLSearchParams();
    
    if (gstPurchaseFilters.dateFrom) {
      params.append('date_from', gstPurchaseFilters.dateFrom.format("YYYY-MM-DD"));
    }
    
    if (gstPurchaseFilters.dateTo) {
      params.append('date_to', gstPurchaseFilters.dateTo.format("YYYY-MM-DD"));
    }
    
    const queryString = params.toString();
    
    setLoadingGstPurchase(true);
    try {
      const response = await api.get(
        `/reports/gst-purchase-report2${queryString ? `?${queryString}` : ''}`
      );
      
      if (response.data && response.data.status) {
        const totalDuty = response.data.records.reduce((sum, item) => sum + item.duty, 0);
        const totalGst = response.data.records.reduce((sum, item) => sum + item.gst, 0);
        const totalQst = response.data.records.reduce((sum, item) => sum + item.qst, 0);

        const dateFromStr = gstPurchaseFilters.dateFrom ? gstPurchaseFilters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = gstPurchaseFilters.dateTo ? gstPurchaseFilters.dateTo.format("YYYY-MM-DD") : "All";
        
        if (exportType === 'excel') {
          const excelData = response.data.records.map(item => ({
            "Date": dayjs(item.date).format("YYYY-MM-DD"),
            "Details": item.details,
            "Amount": item.amount,
            "Duty": item.duty,
            "GST": item.gst,
            "QST": item.qst
          }));
          
          exportToExcel(excelData, `GST_Purchase_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>GST Purchase Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
              h1 { color: #1abc9c; }
              table { border-collapse: collapse; width: 95%; margin-top: 20px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
              .report-summary { margin-bottom: 20px; font-size: 16px; font-weight: bold; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>GST Purchase Report</h1>
              <div class="report-filters">
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD")}</p>
            </div>
            
            
            
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th>$${totalDuty.toFixed(2)}</th>
                  <th>$${totalGst.toFixed(2)}</th>
                  <th>$${totalQst.toFixed(2)}</th>
                </tr>
                <tr>
                  <th>Date</th>
                  <th>Details</th>
                  <th>Amount</th>
                  <th>Duty</th>
                  <th>GST</th>
                  <th>QST</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        response.data.records.forEach(item => {
          htmlContent += `
            <tr>
              <td>${dayjs(item.date).format("YYYY-MM-DD")}</td>
              <td>${item.details}</td>
              <td>$${item.amount.toFixed(2)}</td>
              <td>$${item.duty.toFixed(2)}</td>
              <td>$${item.gst.toFixed(2)}</td>
              <td>$${item.qst.toFixed(2)}</td>
            </tr>
          `;
        });
        
        htmlContent += `
              </tbody>
            </table>
            
            <div class="export-btn-container">
            </div>
          </body>
          </html>
        `;
        
        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `GST_Purchase_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }
        
        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingGstPurchase(false);
          return;
        }
        
        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }
        
        reportWindow.document.write(htmlContent);
        reportWindow.document.close();
        
        window.addEventListener('message', function(event) {
          if (event.data.type === 'exportPDF' && event.data.reportType === 'gstPurchase') {
            generateGstPurchaseReport('pdf');
          } else if (event.data.type === 'exportExcel' && event.data.reportType === 'gstPurchase') {
            generateGstPurchaseReport('excel');
          }
        });
        
        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected filters");
      }
    } catch (error) {
      console.error("Error generating GST purchase report:", error);
      toast.error("Failed to generate GST purchase report");
    } finally {
      setLoadingGstPurchase(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      if (gstPurchaseFilters.dateFrom) params.append('date_from', gstPurchaseFilters.dateFrom.format("YYYY-MM-DD"));
      if (gstPurchaseFilters.dateTo) params.append('date_to', gstPurchaseFilters.dateTo.format("YYYY-MM-DD"));
      
      const queryString = params.toString();
      
      const response = await api.get(`/reports/gst-purchase-report2${queryString ? `?${queryString}` : ''}`);
      
      if (response.data && response.data.status) {
        const totalDuty = response.data.records.reduce((sum, item) => sum + item.duty, 0);
        const totalGst = response.data.records.reduce((sum, item) => sum + item.gst, 0);
        const totalQst = response.data.records.reduce((sum, item) => sum + item.qst, 0);

        const dateFromStr = gstPurchaseFilters.dateFrom ? gstPurchaseFilters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = gstPurchaseFilters.dateTo ? gstPurchaseFilters.dateTo.format("YYYY-MM-DD") : "All";
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>GST Purchase Report</title>
            <style>
               body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center; }
             h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin: 20px; }
              .report-filters { margin: 15px; font-size: 14px; }
              .report-summary { margin-bottom: 20px; font-size: 16px; font-weight: bold; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>GST Purchase Report</h1>
              <div class="report-filters">
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
            <div class="report-summary">
              <p>Total GST: $${response.data.totalGst.toFixed(2)}</p>
              <p>Filtered GST Sum: $${response.data.filteredGstSum.toFixed(2)}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th>$${totalDuty.toFixed(2)}</th>
                  <th>$${totalGst.toFixed(2)}</th>
                  <th>$${totalQst.toFixed(2)}</th>
                </tr>
                <tr>
                  <th>Date</th>
                  <th>Details</th>
                  <th>Amount</th>
                  <th>Duty</th>
                  <th>GST</th>
                  <th>QST</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        response.data.records.forEach(item => {
          htmlContent += `
            <tr>
              <td>${dayjs(item.date).format("YYYY-MM-DD")}</td>
              <td>${item.details}</td>
              <td>$${item.amount.toFixed(2)}</td>
              <td>$${item.duty.toFixed(2)}</td>
              <td>$${item.gst.toFixed(2)}</td>
              <td>$${item.qst.toFixed(2)}</td>
            </tr>
          `;
        });
        
        htmlContent += `
              </tbody>
            </table>
          </body>
          </html>
        `;
        
        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);
        
        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `GST_Purchase_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={5}>
        <DatePicker
          label="From Date"
          value={gstPurchaseFilters.dateFrom}
          onChange={(newValue) => setGstPurchaseFilters({ ...gstPurchaseFilters, dateFrom: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={5}>
        <DatePicker
          label="To Date"
          value={gstPurchaseFilters.dateTo}
          onChange={(newValue) => setGstPurchaseFilters({ ...gstPurchaseFilters, dateTo: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateGstPurchaseReport('html')}
            disabled={loadingGstPurchase || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{ 
              height: "38px", 
              backgroundColor: colors.primary, 
              '&:hover': { 
                backgroundColor: colors.primary, 
                opacity: 0.9 
              } 
            }}
          >
            {loadingGstPurchase ? <CircularProgress size={24} color="inherit" /> : "View"}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateGstPurchaseReport('pdf')}
            disabled={loadingGstPurchase || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateGstPurchaseReport('excel')}
            disabled={loadingGstPurchase || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateGstPurchaseReport('email')}
            disabled={loadingGstPurchase || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default GSTPurchaseReport2;
